import { useEffect, useId, useState, type FormEvent } from 'react';
import type { PromotionDto, PromotionScope, PromotionType } from '@ecom/contracts';
import { usePromotionsStore } from '@/stores/promotions.store';
import { useAuthStore } from '@/stores/auth.store';
import { AsyncMultiSelect, AsyncSelect } from '@/components/async-select';
import { categoryOptions, sellableVariantOptions } from '@/lib/options';
import { useUiStore, type AdminLocale } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import {
  listOf,
  percentLabel,
  promotionDraft,
  promotionRequest,
  promotionState,
  scopeHasTargets,
  type DraftError,
  type PromotionDraft,
  type PromotionField,
  type PromotionState,
} from '@/lib/marketing';
import { PlusIcon, TagIcon } from '@/components/icons';
import { ProductPicker } from '@/components/product-picker';
import { BilingualField, Choices, ToggleField } from '@/components/fields';
import { ListCard, Page, Sheet, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  TableSkeleton,
  type PillTone,
} from '@/components/ui';
import { useDraftError, useWindowLabel } from './marketing-shared';
import { OfferUsageSheet } from '@/components/offer-usage-sheet';
import { useOfferUsageStore } from '@/stores/offer-usage.store';

const STATE_TONES: Record<PromotionState, PillTone> = {
  running: 'success',
  scheduled: 'info',
  ended: 'neutral',
  stopped: 'neutral',
};

const STATE_LABELS = {
  running: 'running',
  scheduled: 'promoStateScheduled',
  ended: 'promoStateEnded',
  stopped: 'stopped',
} as const;

/**
 * Automatic discounts — offers that come off on their own.
 *
 * Separate from codes on purpose: a code is something a shopper types, an
 * automatic discount is a price that is simply true this week. Merging them
 * into one screen would mean half the fields being irrelevant whichever one
 * you opened.
 *
 * The list leads with where each one stands — running, not started yet, over,
 * or stopped — because "switched on but its window closed yesterday" is the
 * state that gets misread. A whole row opens it in a panel from the far edge.
 * Owner-only, like the API: nothing is asked for until the page knows it may.
 */
export function PromotionsPage() {
  const openUsage = useOfferUsageStore((state) => state.open);
  const { items, loading, saving, error, load, deactivate, activate } = usePromotionsStore();
  const isOwner = useAuthStore((state) => state.user?.role === 'OWNER');
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const windowLabel = useWindowLabel();

  const [editing, setEditing] = useState<PromotionDto | 'new' | null>(null);
  const [stopping, setStopping] = useState<PromotionDto | null>(null);

  useEffect(() => {
    if (isOwner) void load();
  }, [isOwner, load]);

  if (!isOwner) {
    return (
      <Page title={t('promotions')} fill>
        <ListCard>
          <EmptyState title={t('ownerOnlySettings')} icon={<TagIcon />} />
        </ListCard>
      </Page>
    );
  }

  const addPromotion = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('addPromotion')}
    </Button>
  );

  return (
    <Page title={t('promotions')} subtitle={t('promotionsSubtitle')} actions={addPromotion} fill>
      <ListCard>
        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={6} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noPromotions')} body={t('noPromotionsHint')} icon={<TagIcon />} action={addPromotion} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('promoColumnName')}</Th>
                  <Th>{t('promoDiscount')}</Th>
                  <Th>{t('promotionScope')}</Th>
                  <Th>{t('discountDates')}</Th>
                  <Th>{t('status')}</Th>
                  <Th align="end">
                    <span className="sr-only">{t('moreActions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {items.map((promotion) => {
                  const state = promotionState(promotion);
                  const shopperName = promotion.translations?.[locale];
                  const conditions = conditionsOf(promotion, locale, t);

                  return (
                    <Tr key={promotion.id} onClick={() => setEditing(promotion)}>
                      <Td>
                        <span className="block font-medium text-ink">{promotion.name}</span>
                        {/* What the shopper reads, when it says something other than the team's name. */}
                        {shopperName && shopperName !== promotion.name ? (
                          <span className="block text-xs text-ink-muted">{shopperName}</span>
                        ) : null}
                      </Td>
                      <Td>
                        <span data-numeric className="block text-ink">
                          {amountOf(promotion, locale, t)}
                        </span>
                        {conditions ? <span className="block text-xs text-ink-muted">{conditions}</span> : null}
                      </Td>
                      <Td className="text-ink-muted">{scopeOf(promotion, locale, t)}</Td>
                      <Td className="whitespace-nowrap text-ink-muted">
                        {windowLabel(promotion.startsAt, promotion.endsAt)}
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1.5">
                          <Pill tone={STATE_TONES[state]}>{t(STATE_LABELS[state])}</Pill>
                          {!promotion.stacksWithCoupons ? <Pill tone="neutral">{t('noCouponStacking')}</Pill> : null}
                        </div>
                      </Td>
                      <Td align="end">
                        <div className="flex justify-end gap-1">
                          {/* What the offer did: the orders it applied to, sold or abandoned (ADR-063). */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              void openUsage({ kind: 'offer', id: promotion.id, name: promotion.name });
                            }}
                          >
                            {t('usageButton')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={saving}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (promotion.isActive) setStopping(promotion);
                              else void activate(promotion.id);
                            }}
                          >
                            {promotion.isActive ? t('stopCampaign') : t('promoStartAgain')}
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </ListCard>

      {editing ? (
        <PromotionEditor
          // One draft per offer: opening another starts from its own values.
          key={editing === 'new' ? 'new' : editing.id}
          promotion={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}

      <OfferUsageSheet />

      <ConfirmDialog
        open={stopping !== null}
        title={t('promoStopTitle')}
        body={t('promoStopBody')}
        confirmLabel={t('stopCampaign')}
        cancelLabel={t('cancel')}
        busy={saving}
        onConfirm={async () => {
          if (stopping) await deactivate(stopping.id);
          setStopping(null);
        }}
        onCancel={() => setStopping(null)}
      />
    </Page>
  );
}

type Translate = ReturnType<typeof useT>;

/** "20%", or "15 SAR per unit" / "50 SAR off the order" — what it takes off. */
function amountOf(promotion: PromotionDto, locale: AdminLocale, t: Translate): string {
  // A set says what it holds and what it costs (ADR-078).
  if (promotion.kind === 'BUNDLE') {
    return t('promoSetSummary', { count: count(promotion.items.length, locale), price: money(promotion.value, locale) });
  }
  // Buy X get Y and tiers say what they are, not a value they do not use (ADR-069).
  if (promotion.kind === 'BUY_X_GET_Y') {
    const reward =
      promotion.getPercent === 100 ? t('promoFree') : t('promoPercentOff', { percent: percentLabel(promotion.getPercent ?? 0, locale) });
    return promotion.buyQuantity === 0
      ? t('promoGiftSummary', { count: count(promotion.getQuantity ?? 1, locale), reward })
      : t('promoBxgySummary', {
          buy: count(promotion.buyQuantity ?? 1, locale),
          get: count(promotion.getQuantity ?? 1, locale),
          reward,
        });
  }
  if (promotion.tiers.length > 0) {
    return promotion.tiers
      .map((tier) =>
        t('promoTierSummary', {
          count: count(tier.minQuantity, locale),
          value: promotion.type === 'PERCENTAGE' ? percentLabel(tier.value, locale) : money(tier.value, locale),
        }),
      )
      .join(' · ');
  }
  if (promotion.type === 'PERCENTAGE') return percentLabel(promotion.value, locale);
  return `${money(promotion.value, locale)} ${promotion.scope === 'ORDER' ? t('offTheOrder') : t('perUnit')}`;
}

/** The conditions and the ceiling, in one quiet line. */
function conditionsOf(promotion: PromotionDto, locale: AdminLocale, t: Translate): string {
  return [
    promotion.minOrderTotal ? t('discountMinOrder', { amount: money(promotion.minOrderTotal, locale) }) : null,
    promotion.minQuantity ? t('promoMinQuantity', { count: count(promotion.minQuantity, locale) }) : null,
    promotion.maxDiscount ? t('promoUpTo', { amount: money(promotion.maxDiscount, locale) }) : null,
    promotion.kind === 'BUY_X_GET_Y' && promotion.getScope
      ? t('promoRewardFrom', {
          where:
            scopeHasTargets(promotion.getScope) && promotion.getTargetNames.some(Boolean)
              ? listOf(promotion.getTargetNames.filter(Boolean), locale)
              : t(`scope${promotion.getScope}` as 'scopeALL'),
        })
      : null,
    promotion.maxApplications
      ? promotion.kind === 'BUNDLE'
        ? `${t('promoSetsPerOrder')}: ${count(promotion.maxApplications, locale)}`
        : t('promoTimesPerOrder', { count: count(promotion.maxApplications, locale) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function scopeOf(promotion: PromotionDto, locale: AdminLocale, t: Translate): string {
  const names = promotion.targetNames.filter(Boolean);
  return scopeHasTargets(promotion.scope) && names.length > 0
    ? listOf(names, locale)
    : t(`scope${promotion.scope}` as 'scopeALL');
}

function PromotionEditor({ promotion, onDone }: { promotion: PromotionDto | null; onDone: () => void }) {
  const { create, update, saving, saveError, clearSaveError } = usePromotionsStore();
  const t = useT();
  const errorText = useDraftError();
  const formId = useId();

  const [draft, setDraft] = useState<PromotionDraft>(() => promotionDraft(promotion));
  const [errors, setErrors] = useState<Partial<Record<PromotionField, DraftError>>>({});

  // A refusal from the last panel is not this one's.
  useEffect(() => {
    clearSaveError();
  }, [clearSaveError]);

  const set = (patch: Partial<PromotionDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as PromotionField[]) delete next[key];
      return next;
    });
  };

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    // A value that cannot be read is said beside its field — never a save that does nothing.
    const result = promotionRequest(draft, promotion);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    const ok = promotion ? await update(promotion.id, result.body) : await create(result.body);
    if (ok) onDone();
  }

  const fixedAtOrder = draft.type === 'FIXED' && draft.scope === 'ORDER';
  const reward = draft.kind === 'BUY_X_GET_Y';
  const bundle = draft.kind === 'BUNDLE';

  return (
    <Sheet
      open
      size="lg"
      title={promotion ? t('editPromotion') : t('addPromotion')}
      subtitle={t('promotionEditorHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button type="submit" form={formId} disabled={saving}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <Field label={t('promotionNameInternal')} htmlFor="promo-name" hint={t('promoNameHint')} error={errorText(errors.name)}>
          <Input
            id="promo-name"
            maxLength={120}
            value={draft.name}
            invalid={Boolean(errors.name)}
            onChange={(event) => set({ name: event.target.value })}
          />
        </Field>

        <BilingualField
          label={t('promotionNameShopper')}
          hint={t('promotionNameShopperHint')}
          value={draft.shopperName}
          onChange={(shopperName) => set({ shopperName })}
          limit={120}
        />

        {/* What the offer does (ADR-069): a discount on lines, or buy X get Y. */}
        <Choices
          legend={t('promoKind')}
          name="promo-kind"
          columns={3}
          options={[
            { value: 'DISCOUNT', label: t('promoKindDiscount'), hint: t('promoKindDiscountHint') },
            { value: 'BUY_X_GET_Y', label: t('promoKindBxgy'), hint: t('promoKindBxgyHint') },
            { value: 'BUNDLE', label: t('promoKindBundle'), hint: t('promoKindBundleHint') },
          ]}
          value={draft.kind}
          onChange={(kind) =>
            set(
              kind === 'BUY_X_GET_Y' && draft.scope === 'ORDER'
                ? { kind, scope: 'ALL', targetIds: [] }
                : kind === 'BUNDLE' && draft.kind !== 'BUNDLE'
                  ? // A set's price is riyals, whatever the discount's type was.
                    { kind, value: '', items: draft.items.length > 0 ? draft.items : [newSetRow(), newSetRow()] }
                  : { kind },
            )
          }
        />

        {reward ? (
          <RewardFields draft={draft} errors={errors} set={set} />
        ) : bundle ? (
          <SetFields draft={draft} errors={errors} set={set} />
        ) : (
        <>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label={t('promotionType')} htmlFor="promo-type">
            <Select
              id="promo-type"
              value={draft.type}
              onChange={(event) => set({ type: event.target.value as PromotionType, value: '' })}
              className="w-full"
            >
              <option value="PERCENTAGE">{t('typePercentage')}</option>
              <option value="FIXED">{t('typeFIXED')}</option>
            </Select>
          </Field>

          {draft.tiered ? (
            <p className="self-end rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">{t('promoTiersBelow')}</p>
          ) : (
            <Field
              label={
                draft.type === 'PERCENTAGE' ? t('percentOff') : fixedAtOrder ? t('promoAmountOffOrder') : t('amountOffPerUnit')
              }
              htmlFor="promo-value"
              hint={draft.type === 'PERCENTAGE' ? t('valuePercentHint') : t('valueFixedHint')}
              error={errorText(errors.value)}
            >
              <Input
                id="promo-value"
                dir="ltr"
                inputMode={draft.type === 'PERCENTAGE' ? 'numeric' : 'decimal'}
                value={draft.value}
                invalid={Boolean(errors.value)}
                onChange={(event) => set({ value: event.target.value })}
              />
            </Field>
          )}

          <Field
            label={t('minOrderTotal')}
            htmlFor="promo-min"
            hint={t('discountMinOrderHint')}
            error={errorText(errors.minOrderTotal)}
          >
            <Input
              id="promo-min"
              dir="ltr"
              inputMode="decimal"
              value={draft.minOrderTotal}
              invalid={Boolean(errors.minOrderTotal)}
              onChange={(event) => set({ minOrderTotal: event.target.value })}
            />
          </Field>
        </div>

        {/* "2 for 10%, 3 for 15%" — the units reached decide the value (ADR-069). */}
        <ToggleField
          label={t('promoTiered')}
          hint={t('promoTieredHint')}
          checked={draft.tiered}
          onChange={(tiered) => set({ tiered })}
        />
        {draft.tiered ? <TierRows draft={draft} error={errorText(errors.tiers)} set={set} /> : null}
        </>
        )}

        {bundle ? null : (
        <>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={reward ? t('promoBuyScope') : t('promotionScope')} htmlFor="promo-scope" hint={reward ? t('promoBuyScopeHint') : undefined}>
            <Select
              id="promo-scope"
              value={draft.scope}
              onChange={(event) => set({ scope: event.target.value as PromotionScope, targetIds: [] })}
              className="w-full"
            >
              <option value="ALL">{t('scopeALL')}</option>
              {/* Buy X get Y is about products, never the order as a whole. */}
              {reward ? null : <option value="ORDER">{t('scopeORDER')}</option>}
              <option value="CATEGORY">{t('scopeCATEGORY')}</option>
              <option value="PRODUCT">{t('scopePRODUCT')}</option>
            </Select>
          </Field>

          {draft.scope === 'CATEGORY' ? (
            <Field
              label={t('categories')}
              htmlFor="promo-targets"
              hint={t('categoryScopeHint')}
              error={errors.targetIds ? t('promoTargetsRequired') : undefined}
            >
              <AsyncMultiSelect
                id="promo-targets"
                values={draft.targetIds}
                onChange={(targetIds) => set({ targetIds })}
                fetchPage={categoryOptions.page}
                fetchOne={categoryOptions.one}
              />
            </Field>
          ) : null}
        </div>

        {draft.scope === 'PRODUCT' ? (
          <div className="flex flex-col gap-1.5">
            <ProductPicker
              label={t('products')}
              hint={t('productScopeHint')}
              selectedIds={draft.targetIds}
              onChange={(targetIds) => set({ targetIds })}
            />
            {errors.targetIds ? (
              <p role="alert" className="text-xs text-danger">
                {t('promoTargetsRequired')}
              </p>
            ) : null}
          </div>
        ) : null}
        </>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t('startsAt')} htmlFor="promo-start" hint={t('discountStartsHint')} error={errorText(errors.startsAt)}>
            <Input
              id="promo-start"
              type="date"
              dir="ltr"
              value={draft.startsAt}
              invalid={Boolean(errors.startsAt)}
              onChange={(event) => set({ startsAt: event.target.value })}
            />
          </Field>
          <Field label={t('endsAt')} htmlFor="promo-end" hint={t('discountEndsHint')} error={errorText(errors.endsAt)}>
            <Input
              id="promo-end"
              type="date"
              dir="ltr"
              value={draft.endsAt}
              invalid={Boolean(errors.endsAt)}
              onChange={(event) => set({ endsAt: event.target.value })}
            />
          </Field>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {bundle ? null : (
            <Field label={t('maxDiscount')} htmlFor="promo-cap" hint={t('maxDiscountHint')} error={errorText(errors.maxDiscount)}>
              <Input
                id="promo-cap"
                dir="ltr"
                inputMode="decimal"
                value={draft.maxDiscount}
                invalid={Boolean(errors.maxDiscount)}
                onChange={(event) => set({ maxDiscount: event.target.value })}
              />
            </Field>
          )}
          {reward || bundle ? (
            <Field
              label={bundle ? t('promoSetsPerOrder') : t('promoMaxApplications')}
              htmlFor="promo-times"
              hint={bundle ? t('promoSetsPerOrderHint') : t('promoMaxApplicationsHint')}
              error={errorText(errors.maxApplications)}
            >
              <Input
                id="promo-times"
                dir="ltr"
                inputMode="numeric"
                value={draft.maxApplications}
                invalid={Boolean(errors.maxApplications)}
                onChange={(event) => set({ maxApplications: event.target.value })}
              />
            </Field>
          ) : (
            <Field
              label={t('minQuantity')}
              htmlFor="promo-minqty"
              hint={t('minQuantityHint')}
              error={errorText(errors.minQuantity)}
            >
              <Input
                id="promo-minqty"
                dir="ltr"
                inputMode="numeric"
                value={draft.minQuantity}
                invalid={Boolean(errors.minQuantity)}
                onChange={(event) => set({ minQuantity: event.target.value })}
              />
            </Field>
          )}
          <Field label={t('priority')} htmlFor="promo-priority" hint={t('priorityHint')} error={errorText(errors.priority)}>
            <Input
              id="promo-priority"
              dir="ltr"
              inputMode="numeric"
              value={draft.priority}
              invalid={Boolean(errors.priority)}
              onChange={(event) => set({ priority: event.target.value })}
            />
          </Field>
        </div>

        <ToggleField
          label={t('allowCouponStacking')}
          hint={t('allowCouponStackingHint')}
          checked={draft.stacksWithCoupons}
          onChange={(stacksWithCoupons) => set({ stacksWithCoupons })}
        />

        {Object.keys(errors).length > 0 ? <Alert>{t('mktCheckFields')}</Alert> : null}
        {/* The API's refusals name the problem, so they sit inside the form that met them. */}
        {saveError ? <Alert>{saveError}</Alert> : null}
      </form>
    </Sheet>
  );
}

type DraftSetter = (patch: Partial<PromotionDraft>) => void;

function newSetRow(): PromotionDraft['items'][number] {
  return { variantId: '', label: '', quantity: '1' };
}

/**
 * A set at its own price (ADR-078): its things — searched variants, each with
 * how many — and the price the whole set sells for.
 */
function SetFields({
  draft,
  errors,
  set,
}: {
  draft: PromotionDraft;
  errors: Partial<Record<PromotionField, DraftError>>;
  set: DraftSetter;
}) {
  const t = useT();
  const errorText = useDraftError();
  const rows = draft.items;
  const update = (index: number, patch: Partial<PromotionDraft['items'][number]>) =>
    set({ items: rows.map((row, at) => (at === index ? { ...row, ...patch } : row)) });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-ink">{t('promoSetItems')}</p>
        <p className="text-xs text-ink-muted">{t('promoSetItemsHint')}</p>
        {rows.map((row, index) => (
          <div key={index} className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <AsyncSelect
                id={`set-item-${index}`}
                value={row.variantId}
                placeholder={t('promoSetPick')}
                allowClear={false}
                fetchPage={sellableVariantOptions.page}
                // The row remembers what was picked, so an edit opens with names rather than ids.
                fetchOne={async (id) => (id && id === row.variantId && row.label ? { id, label: row.label } : null)}
                onChange={(variantId, option) => update(index, { variantId, label: option?.label ?? '' })}
              />
            </div>
            <Input
              aria-label={t('promoSetQuantity')}
              dir="ltr"
              inputMode="numeric"
              value={row.quantity}
              onChange={(event) => update(index, { quantity: event.target.value.replace(/[^0-9]/g, '') })}
              className="w-20"
            />
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              disabled={rows.length <= 2}
              onClick={() => set({ items: rows.filter((_, at) => at !== index) })}
            >
              {t('promoSetRemove')}
            </Button>
          </div>
        ))}
        {errors.items ? (
          <p role="alert" className="text-xs text-danger">
            {errorText(errors.items)}
          </p>
        ) : null}
        <div>
          <Button variant="secondary" size="sm" disabled={rows.length >= 10} onClick={() => set({ items: [...rows, newSetRow()] })}>
            {t('promoSetAddItem')}
          </Button>
        </div>
      </div>

      <Field label={t('promoSetPrice')} htmlFor="promo-value" hint={t('promoSetPriceHint')} error={errorText(errors.value)}>
        <Input
          id="promo-value"
          dir="ltr"
          inputMode="decimal"
          value={draft.value}
          invalid={Boolean(errors.value)}
          onChange={(event) => set({ value: event.target.value })}
          className="max-w-48"
        />
      </Field>
    </div>
  );
}

/**
 * Buy X get Y (ADR-069): how many are bought, how many rewarded and at what,
 * the basket a gift needs, and where the reward comes from.
 */
function RewardFields({
  draft,
  errors,
  set,
}: {
  draft: PromotionDraft;
  errors: Partial<Record<PromotionField, DraftError>>;
  set: DraftSetter;
}) {
  const t = useT();
  const errorText = useDraftError();
  const gift = draft.buyQuantity.trim() === '0';

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 md:grid-cols-3">
        <Field label={t('promoBuyQuantity')} htmlFor="promo-buy" hint={t('promoBuyQuantityHint')} error={errorText(errors.buyQuantity)}>
          <Input
            id="promo-buy"
            dir="ltr"
            inputMode="numeric"
            value={draft.buyQuantity}
            invalid={Boolean(errors.buyQuantity)}
            onChange={(event) => set({ buyQuantity: event.target.value })}
          />
        </Field>
        <Field label={t('promoGetQuantity')} htmlFor="promo-get" hint={t('promoGetQuantityHint')} error={errorText(errors.getQuantity)}>
          <Input
            id="promo-get"
            dir="ltr"
            inputMode="numeric"
            value={draft.getQuantity}
            invalid={Boolean(errors.getQuantity)}
            onChange={(event) => set({ getQuantity: event.target.value })}
          />
        </Field>
        <Field label={t('promoGetPercent')} htmlFor="promo-get-percent" hint={t('promoGetPercentHint')} error={errorText(errors.getPercent)}>
          <Input
            id="promo-get-percent"
            dir="ltr"
            inputMode="numeric"
            value={draft.getPercent}
            invalid={Boolean(errors.getPercent)}
            onChange={(event) => set({ getPercent: event.target.value })}
          />
        </Field>
      </div>

      <Field
        label={t('minOrderTotal')}
        htmlFor="promo-min"
        hint={gift ? t('promoGiftMinimumHint') : t('discountMinOrderHint')}
        error={errorText(errors.minOrderTotal)}
      >
        <Input
          id="promo-min"
          dir="ltr"
          inputMode="decimal"
          value={draft.minOrderTotal}
          invalid={Boolean(errors.minOrderTotal)}
          onChange={(event) => set({ minOrderTotal: event.target.value })}
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <Field label={t('promoRewardScope')} htmlFor="promo-get-scope" hint={t('promoRewardScopeHint')}>
          <Select
            id="promo-get-scope"
            value={draft.getScope}
            onChange={(event) => set({ getScope: event.target.value as PromotionDraft['getScope'], getTargetIds: [] })}
            className="w-full"
          >
            <option value="">{t('promoRewardSame')}</option>
            <option value="ALL">{t('scopeALL')}</option>
            <option value="CATEGORY">{t('scopeCATEGORY')}</option>
            <option value="PRODUCT">{t('scopePRODUCT')}</option>
          </Select>
        </Field>
        {draft.getScope === 'CATEGORY' ? (
          <Field
            label={t('categories')}
            htmlFor="promo-get-targets"
            error={errors.getTargetIds ? t('promoTargetsRequired') : undefined}
          >
            <AsyncMultiSelect
              id="promo-get-targets"
              values={draft.getTargetIds}
              onChange={(getTargetIds) => set({ getTargetIds })}
              fetchPage={categoryOptions.page}
              fetchOne={categoryOptions.one}
            />
          </Field>
        ) : null}
      </div>
      {draft.getScope === 'PRODUCT' ? (
        <div className="flex flex-col gap-1.5">
          <ProductPicker
            label={t('promoRewardProducts')}
            selectedIds={draft.getTargetIds}
            onChange={(getTargetIds) => set({ getTargetIds })}
          />
          {errors.getTargetIds ? (
            <p role="alert" className="text-xs text-danger">
              {t('promoTargetsRequired')}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Quantity tiers, one row each: from how many units, and the value from there. */
function TierRows({ draft, error, set }: { draft: PromotionDraft; error: string | undefined; set: DraftSetter }) {
  const t = useT();
  const percent = draft.type === 'PERCENTAGE';
  const update = (index: number, patch: Partial<PromotionDraft['tiers'][number]>) =>
    set({ tiers: draft.tiers.map((tier, at) => (at === index ? { ...tier, ...patch } : tier)) });

  return (
    <fieldset className="flex flex-col gap-2 rounded-control border border-line p-4">
      <legend className="px-1 text-sm font-medium text-ink">{t('promoTiers')}</legend>
      {draft.tiers.map((tier, index) => (
        <div key={index} className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
          <Field label={t('promoTierFrom')} htmlFor={`tier-min-${index}`}>
            <Input
              id={`tier-min-${index}`}
              dir="ltr"
              inputMode="numeric"
              value={tier.minQuantity}
              onChange={(event) => update(index, { minQuantity: event.target.value })}
            />
          </Field>
          <Field label={percent ? t('percentOff') : t('amountOffPerUnit')} htmlFor={`tier-value-${index}`}>
            <Input
              id={`tier-value-${index}`}
              dir="ltr"
              inputMode={percent ? 'numeric' : 'decimal'}
              value={tier.value}
              onChange={(event) => update(index, { value: event.target.value })}
            />
          </Field>
          <Button
            variant="ghost"
            size="sm"
            disabled={draft.tiers.length <= 1}
            onClick={() => set({ tiers: draft.tiers.filter((_, at) => at !== index) })}
          >
            {t('remove')}
          </Button>
        </div>
      ))}
      {error ? (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={draft.tiers.length >= 10}
          onClick={() => {
            const last = Number(draft.tiers.at(-1)?.minQuantity) || 1;
            set({ tiers: [...draft.tiers, { minQuantity: String(last + 1), value: '' }] });
          }}
        >
          <PlusIcon className="size-4" />
          {t('promoAddTier')}
        </Button>
      </div>
    </fieldset>
  );
}
